import React from "react";
import { Link, useInRouterContext } from "react-router-dom";

/**
 * A Staff Assistant answer with its family links clickable (PM 2902e73d).
 *
 * The assistant links every family it answers about to that family's page,
 * as a markdown link `[Melissa Cadd](/club/manage/memberships/<id>)` or a bare
 * path. The panel rendered answers as plain text, so the link read as
 * markdown punctuation. Only the admin app's own pages (/<club>/manage/...)
 * become links; any other target stays plain text, so a reply can never
 * carry a javascript: or off-site link.
 */
const INTERNAL_PATH = /^\/[A-Za-z0-9-]+\/manage\/[A-Za-z0-9/_-]+$/;
const LINK_PATTERN = /\[([^\]\n]+)\]\(([^)\s]+)\)|(\/[A-Za-z0-9-]+\/manage\/[A-Za-z0-9/_-]+)/g;

export interface AnswerPart {
  text: string;
  href?: string;
}

/** Split an answer into text and internal links. Pure, for tests. */
export function splitStaffAnswer(content: string): AnswerPart[] {
  const parts: AnswerPart[] = [];
  let last = 0;
  const pattern = new RegExp(LINK_PATTERN.source, "g");
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(content)) !== null) {
    const at = m.index;
    if (at > last) parts.push({ text: content.slice(last, at) });
    const [whole, label, target, bare] = m;
    const href = target || bare;
    if (href && INTERNAL_PATH.test(href)) parts.push({ text: label || href, href });
    else parts.push({ text: whole });
    last = at + whole.length;
  }
  if (last < content.length) parts.push({ text: content.slice(last) });
  return parts;
}

const linkClass = "underline font-medium text-[var(--cc-link)] hover:opacity-80";

const StaffAnswerText: React.FC<{ content: string }> = ({ content }) => {
  const inRouter = useInRouterContext();
  return (
    <>
      {splitStaffAnswer(content).map((part, i) => {
        if (!part.href) return <React.Fragment key={i}>{part.text}</React.Fragment>;
        return inRouter ? (
          <Link key={i} to={part.href} className={linkClass}>
            {part.text}
          </Link>
        ) : (
          <a key={i} href={part.href} className={linkClass}>
            {part.text}
          </a>
        );
      })}
    </>
  );
};

export default StaffAnswerText;
